import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import useTranslation from "../../hooks/i18n/useTranslation";
import { usePortal } from "../../classes/contexts/PortalContext";
import IconsEnum from "../../classes/enums/IconsEnum";
import IconButton from "../iconButton/IconButton";
import Select from "../select/Select";
import "./ValidationPointLabel.css";

const ValidationPointLabel = ({
  pointIndex,
  viewId,
  position,
  color = "#ff0000",
  centroids = [],
  selectedCentroid = null,
  onCentroidChange,
  onDelete,
  onPositionChange,
}) => {
  const { t } = useTranslation();
  const portalRoot = usePortal();
  const [isDragging, setIsDragging] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(position);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const labelRef = useRef(null);

  // Update position when prop changes
  useEffect(() => {
    setCurrentPosition(position);
  }, [position]);

  // Handle dragging
  useEffect(() => {
    const handleMouseMove = e => {
      if (isDragging) {
        let newX = e.clientX - dragOffset.x;
        let newY = e.clientY - dragOffset.y;

        // Clamp to window bounds
        if (labelRef.current) {
          const { width, height } = labelRef.current.getBoundingClientRect();

          if (newX < 0) newX = 0;
          if (newY < 0) newY = 0;
          if (newX + width > window.innerWidth) {
            newX = window.innerWidth - width;
          }
          if (newY + height > window.innerHeight) {
            newY = window.innerHeight - height;
          }
        }

        const newPosition = { x: newX, y: newY };
        setCurrentPosition(newPosition);

        // Notify parent of position change
        if (onPositionChange) {
          onPositionChange(pointIndex, viewId, newPosition);
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset, pointIndex, onPositionChange]);

  const startDragging = e => {
    // Only start dragging for left mouse button
    if (e.button !== 0) return;
    e.preventDefault();
    // Do not call stopPropagation here, so child controls can prevent drag
    const rect = labelRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const handleCentroidSelect = event => {
    // The Select component passes a synthetic event, check if stopPropagation exists
    if (event.stopPropagation) {
      event.stopPropagation();
    }
    const newValue = event.target.value;
    if (onCentroidChange && newValue) {
      onCentroidChange(pointIndex, parseInt(newValue));
    }
  };

  const handleDeleteClick = e => {
    if (e.preventDefault) {
      e.preventDefault();
    }
    if (e.stopPropagation) {
      e.stopPropagation();
    }
    if (onDelete) {
      onDelete(pointIndex);
    }
  };

  if (!portalRoot) return null;

  const labelContent = (
    <div
      ref={labelRef}
      className="ValidationPointLabel"
      onMouseDown={startDragging}
      style={{
        left: `${currentPosition.x}px`,
        top: `${currentPosition.y}px`,
        cursor: isDragging ? "grabbing" : "grab",
        // borderColor: color,
      }}
    >
      <div className="ValidationPointLabel-content">
        <div className="ValidationPointLabel-controls">
          <div
            className="ValidationPointLabel-select"
            onMouseDown={e => e.stopPropagation()}
          >
            <Select
              items={centroids}
              selectedItem={selectedCentroid}
              placeholder="Select centroid..."
              onChange={handleCentroidSelect}
              disable={false}
            />
          </div>
          <span onMouseDown={e => e.stopPropagation()}>
            <IconButton
              icon={IconsEnum.DELETE}
              onClick={handleDeleteClick}
              size="small"
              variant="danger"
              noBgColor
              title={t("4")}
            />
          </span>
        </div>
      </div>
      <div
        className="ValidationPointLabel-indicator"
        style={{ backgroundColor: color }}
      />
    </div>
  );

  return ReactDOM.createPortal(labelContent, portalRoot);
};

export default ValidationPointLabel;
